import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  useGetKadryRegistryQuery,
  useListKadrySearchRunsQuery,
  useListKadryVacancyLinksQuery,
  useRunKadrySearchMutation,
} from '@shared/api';
import {
  useKadryAccess,
  type KadryHiringRequest,
} from '@entities/kadry';
import {
  KadryCandidateFinder,
  KadryCardHead,
  KadryErrorBanner,
  KadryMuted,
  kadryCandidateMatches,
  kadrySortCandidates,
  useKadryCandidateSort,
} from '../../ui';
import { Toolbar, Summary, List, Collapse } from './styles';
import { SORTS, runLine } from './types';
import { useWasTrue } from './useWasTrue';
import { VacancyEditor } from './VacancyEditor';
import { CandidateRow } from './CandidateRow';

export function CandidatesPanelView({ request }: { request: KadryHiringRequest }) {
  const { can, canWrite } = useKadryAccess();
  const full = request.access === 'full';
  const runs = useListKadrySearchRunsQuery(request.id);
  const lastRun = runs.data?.[0];
  const running = lastRun?.status === 'running';
  const registry = useGetKadryRegistryQuery(request.id, {
    // Ответы кандидатов приходят и через WebSocket; опрос — страховка и
    // ход поиска.
    pollingInterval: running ? 5000 : 30000,
  });
  const vacancies = useListKadryVacancyLinksQuery(request.id);
  const [runSearch, runState] = useRunKadrySearchMutation();
  const [showFiltered, setShowFiltered] = useState(false);
  const [sort, setSort] = useKadryCandidateSort('registry', SORTS, 'answer');
  const [query, setQuery] = useState('');

  // Пока идёт поиск — следим за прогоном чаще; по окончании обновляем реестр.
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => runs.refetch(), 4000);
    return () => clearInterval(t);
  }, [running, runs]);
  const wasRunning = useWasTrue(running);
  useEffect(() => {
    if (wasRunning && !running) registry.refetch();
  }, [wasRunning, running, registry]);

  const hhVacancy = vacancies.data?.find((v) => v.source === 'hh');
  const rabotaVacancy = vacancies.data?.find((v) => v.source === 'rabota');
  const rows = registry.data?.rows ?? [];
  const found = useMemo(() => rows.filter((r) => kadryCandidateMatches(r, query)), [rows, query]);
  const passed = useMemo(
    () => kadrySortCandidates(found.filter((r) => r.match.passed_filters), sort),
    [found, sort],
  );
  const filtered = useMemo(
    () => kadrySortCandidates(found.filter((r) => !r.match.passed_filters), sort),
    [found, sort],
  );
  const searching = query.trim().length > 0;
  const canSearch = canWrite('search_run') && full && request.status === 'active';
  const canSend = canWrite('paid_action') && full && request.status === 'active';
  const canMark = canWrite('match_status_change') && full;
  const canEditVacancy = canWrite('request_manage') && full;
  const s = registry.data?.summary;

  return (
    <Card>
      <KadryCardHead>
        <CardTitle>Кандидаты</CardTitle>
        {canSearch && (
          <Button
            type="button"
            $size="sm"
            disabled={running || runState.isLoading}
            onClick={() => runSearch(request.id)}
          >
            {running ? 'Ищем…' : 'Искать сейчас'}
          </Button>
        )}
      </KadryCardHead>

      <Toolbar>
        <span>{runLine(lastRun)}</span>
        {lastRun?.report.sources?.some(
          (x) => (x.source === 'hh' || x.source === 'rabota') && x.status === 'skipped',
        ) &&
          can('admin_settings') && <Link to="/agents/kadry/settings/connections">Настроить подключения</Link>}
      </Toolbar>
      {runState.error ? (
        <KadryErrorBanner error={runState.error} onDismiss={() => runState.reset()} />
      ) : null}

      {full && (
        <>
          <VacancyEditor requestId={request.id} source="hh" link={hhVacancy} canEdit={canEditVacancy} />
          <VacancyEditor
            requestId={request.id}
            source="rabota"
            link={rabotaVacancy}
            canEdit={canEditVacancy}
          />
        </>
      )}
      {!hhVacancy && !rabotaVacancy && full && (
        <KadryMuted>
          Приглашения на площадках отправляются на опубликованную вакансию компании — укажите
          ссылку на вакансию hh.ru и (или) Работа.ру. Без неё предложение уйдёт только в ботов
          тем, кто уже подключал бота.
        </KadryMuted>
      )}

      {s && s.candidates > 0 && (
        <Summary>
          <Badge $tone="neutral">кандидатов {s.candidates}</Badge>
          <Badge $tone="info">предложений {s.offers_sent}</Badge>
          <Badge $tone="success">подходит {s.interested}</Badge>
          <Badge $tone="neutral">не подходит {s.not_interested}</Badge>
          {s.hr_not_contacted > 0 && (
            <Badge $tone="danger">не связались {s.hr_not_contacted}</Badge>
          )}
        </Summary>
      )}

      {registry.isLoading ? (
        <>
          <SkeletonLine $width="80%" />
          <SkeletonLine $width="65%" />
        </>
      ) : registry.error ? (
        <KadryErrorBanner error={registry.error} onRefresh={registry.refetch} />
      ) : rows.length === 0 ? (
        <KadryMuted>
          {request.status === 'active'
            ? 'Кандидатов пока нет. Поиск запускается автоматически после утверждения профиля и повторяется по расписанию.'
            : 'Поиск кандидатов идёт по активной заявке с утверждённым профилем.'}
        </KadryMuted>
      ) : (
        <>
          <KadryCandidateFinder
            query={query}
            onQuery={setQuery}
            sort={sort}
            onSort={setSort}
            options={SORTS}
            found={found.length}
            total={rows.length}
            placeholder={
              full
                ? 'Поиск: ФИО, телефон, должность, почта, комментарий'
                : 'Поиск по должности из резюме'
            }
          />
          {searching && found.length === 0 && <KadryMuted>Никого не нашли — проверьте написание.</KadryMuted>}
          <List>
            {passed.map((r) => (
              <CandidateRow
                key={r.match.id}
                row={r}
                requestId={request.id}
                canSend={canSend}
                canMark={canMark}
              />
            ))}
          </List>
          {filtered.length > 0 && (
            <>
              {searching ? (
                <KadryMuted>Не прошли обязательные требования ({filtered.length}):</KadryMuted>
              ) : (
                <Collapse type="button" onClick={() => setShowFiltered((v) => !v)}>
                  {showFiltered ? 'Скрыть' : 'Показать'} не прошедших обязательные требования (
                  {filtered.length})
                </Collapse>
              )}
              {(showFiltered || searching) && (
                <List>
                  {filtered.map((r) => (
                    <CandidateRow
                      key={r.match.id}
                      row={r}
                      requestId={request.id}
                      canSend={canSend}
                      canMark={canMark}
                    />
                  ))}
                </List>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}
